<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
const props = defineProps({
  profile: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update']);
const { t } = useI18n();

const fields = computed(() => ({
  cep: {
    label: t('SALES_PROPERTY.ADDRESS.CEP'),
    placeholder: t('SALES_PROPERTY.ADDRESS.CEP_PLACEHOLDER'),
    readOnly: true,
    required: true,
  },
  logradouro: {
    label: t('SALES_PROPERTY.ADDRESS.STREET'),
    placeholder: t('SALES_PROPERTY.ADDRESS.STREET_PLACEHOLDER'),
    readOnly: true,
    required: true,
  },
  numero: {
    label: t('SALES_PROPERTY.ADDRESS.NUMBER'),
    placeholder: t('SALES_PROPERTY.ADDRESS.NUMBER_PLACEHOLDER'),
    required: true,
  },
  unidade: {
    label: t('SALES_PROPERTY.ADDRESS.UNIT'),
    placeholder: t('SALES_PROPERTY.ADDRESS.UNIT_PLACEHOLDER'),
  },
  complemento: {
    label: t('SALES_PROPERTY.ADDRESS.COMPLEMENT'),
    placeholder: t('SALES_PROPERTY.ADDRESS.COMPLEMENT_PLACEHOLDER'),
  },
  cidade: {
    label: t('SALES_PROPERTY.ADDRESS.CITY'),
    placeholder: t('SALES_PROPERTY.ADDRESS.CITY_PLACEHOLDER'),
    readOnly: true,
    required: true,
  },
  estado: {
    label: t('SALES_PROPERTY.ADDRESS.STATE'),
    placeholder: t('SALES_PROPERTY.ADDRESS.STATE_PLACEHOLDER'),
    readOnly: true,
    required: true,
  },
  bairro: {
    label: t('SALES_PROPERTY.ADDRESS.NEIGHBORHOOD'),
    placeholder: t('SALES_PROPERTY.ADDRESS.NEIGHBORHOOD_PLACEHOLDER'),
    readOnly: true,
    required: true,
  },
}));
const fieldGroups = computed(() => [
  { keys: ['cep', 'logradouro'], columns: 2 },
  { keys: ['numero', 'unidade', 'complemento'], columns: 3 },
  { keys: ['cidade', 'estado', 'bairro'], columns: 3 },
]);

const update = (field, value) => emit('update', field, value);
const hasValue = key => Boolean(props.profile[key]);
</script>

<template>
  <section
    class="rounded-[10px] bg-white p-8 font-roboto shadow-sm max-[700px]:p-8"
    data-test="sales-property-address-section"
  >
    <div class="grid gap-[22px] max-[700px]:gap-7">
      <header>
        <h2
          class="font-lato text-[32px] font-bold leading-[39px] text-[#464F53] max-[700px]:text-[24px] max-[700px]:leading-[29px]"
        >
          {{ t('SALES_PROPERTY.ADDRESS.TITLE') }}
        </h2>
        <p
          class="mt-2 mb-0 text-[20px] leading-6 text-[#464F53] max-[700px]:mb-4"
        >
          {{ t('SALES_PROPERTY.ADDRESS.DESCRIPTION') }}
        </p>
      </header>
      <div class="grid gap-4">
        <div
          v-for="(group, groupIndex) in fieldGroups"
          :key="groupIndex"
          class="grid gap-4"
          :class="
            group.columns === 2
              ? 'min-[701px]:grid-cols-2'
              : 'min-[701px]:grid-cols-3'
          "
          :data-test="`sales-property-address-group-${groupIndex}`"
        >
          <label
            v-for="key in group.keys"
            :key="key"
            class="relative flex h-[82px] min-w-0 flex-col justify-center rounded-[10px] px-4 pt-3"
            :class="
              errors[key]
                ? 'border border-lais-danger bg-white'
                : fields[key].readOnly && hasValue(key)
                  ? 'bg-[#F1F4F5]'
                  : hasValue(key)
                    ? 'bg-[#EDF8F7]'
                    : 'border border-lais-border bg-white'
            "
            :for="`sales-property-address-${key}`"
          >
            <span
              class="text-xs text-[#64748B]"
              :class="
                fields[key].required ? `after:ml-0 after:content-['*']` : ''
              "
            >
              {{ fields[key].label }}
            </span>
            <input
              :id="`sales-property-address-${key}`"
              :value="props.profile[key]"
              :placeholder="fields[key].placeholder"
              :disabled="fields[key].readOnly"
              class="mt-1 w-full border-b bg-transparent pb-1 pr-7 text-base font-bold text-[#64748B] outline-none placeholder:text-[#B4C0C8] disabled:cursor-default disabled:text-[#64748B]"
              :class="
                errors[key]
                  ? 'border-lais-danger'
                  : fields[key].readOnly
                    ? 'border-[#A7B7BE]'
                    : hasValue(key)
                      ? 'border-[#009F94]'
                      : 'border-[#5D696F]'
              "
              :data-test="`sales-property-address-${key}`"
              @input="update(key, $event.target.value)"
            />
            <span
              v-if="hasValue(key) && !errors[key]"
              class="i-lucide-check absolute bottom-5 right-4 size-5 text-[#009F94]"
              aria-hidden="true"
            />
            <span v-if="errors[key]" class="mt-1 text-xs text-lais-danger">
              {{ errors[key] }}
            </span>
          </label>
        </div>
      </div>
    </div>
    <slot name="footer" />
  </section>
</template>
